/* ============================================================
   MINIMA — theme interactions (vanilla JS)
   ============================================================ */
(function () {
  'use strict';

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  /* ---------- Scroll reveal ----------
     rAF-throttled position check on scroll/resize — deterministic in every
     environment (some embedded webviews never fire IntersectionObserver).
     Fail-safe: if the viewport reports no height (headless/offscreen renderers),
     reveal everything immediately — content must never stay hidden. */
  const viewportH = () =>
    window.innerHeight || document.documentElement.clientHeight || 0;

  function initReveal() {
    let els = $$('[data-reveal]:not(.is-visible)');
    if (prefersReduced || !viewportH()) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    let ticking = false;
    const check = () => {
      ticking = false;
      const limit = viewportH() - 60;
      els = els.filter((el) => {
        if (el.getBoundingClientRect().top < limit) {
          el.classList.add('is-visible');
          return false;
        }
        return true;
      });
      if (!els.length) {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
        clearInterval(heartbeat);
      }
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(check);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    // Heartbeat fallback for environments where scroll events don't fire
    const heartbeat = setInterval(() => {
      if (!els.length) clearInterval(heartbeat);
      else check();
    }, 700);
    check();
  }

  /* ---------- Header scroll state ---------- */
  function initHeader() {
    const header = $('[data-header]');
    if (!header) return;
    const isHome = header.hasAttribute('data-home');
    const apply = () => {
      const solid = window.scrollY > 24 || !isHome;
      header.classList.toggle('header--solid', solid);
      header.classList.toggle('header--transparent', !solid);
    };
    apply();
    window.addEventListener('scroll', apply, { passive: true });
  }

  /* ---------- Mobile nav ---------- */
  function initMobileNav() {
    const nav = $('[data-mobilenav]');
    if (!nav) return;
    const open = () => {
      nav.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    };
    const close = () => {
      nav.classList.remove('is-open');
      document.body.style.overflow = '';
    };
    $$('[data-mobilenav-open]').forEach((b) => b.addEventListener('click', open));
    $$('[data-mobilenav-close]').forEach((b) => b.addEventListener('click', close));
    $('[data-mobilenav-scrim]', nav)?.addEventListener('click', close);
  }

  /* ---------- Hero typing ---------- */
  function initHeroTyping() {
    const el = $('[data-typing]');
    if (!el) return;
    const text = el.getAttribute('data-typing') || '';
    const caret = '<span class="hero__caret"></span>';
    if (prefersReduced) {
      el.innerHTML = text + caret;
      return;
    }
    let i = 0;
    el.innerHTML = caret;
    setTimeout(() => {
      const timer = setInterval(() => {
        i++;
        el.innerHTML = text.slice(0, i) + caret;
        if (i >= text.length) clearInterval(timer);
      }, 90);
    }, 500);
  }

  /* ---------- Before / After slider ---------- */
  function initBeforeAfter() {
    $$('[data-ba]').forEach((ba) => {
      const handle = $('[data-ba-handle]', ba);
      const getPos = () => parseFloat(ba.style.getPropertyValue('--pos')) || 50;
      const setPos = (clientX, animate) => {
        const rect = ba.getBoundingClientRect();
        let pct = ((clientX - rect.left) / rect.width) * 100;
        pct = Math.max(2, Math.min(98, pct));
        ba.classList.toggle('is-animating', !!animate && !prefersReduced);
        ba.classList.add('is-touched');
        ba.style.setProperty('--pos', pct + '%');
        handle?.setAttribute('aria-valuenow', String(Math.round(pct)));
      };

      // Pointer logic: mouse drags freely; touch only claims the gesture when it is
      // clearly horizontal (or starts on the handle) so vertical page scroll stays smooth.
      let dragging = false;
      let pending = null;
      const onDown = (e) => {
        if (e.pointerType === 'mouse') {
          dragging = true;
          setPos(e.clientX);
        } else if (handle && (e.target === handle || handle.contains(e.target))) {
          dragging = true;
        } else {
          pending = { x: e.clientX, y: e.clientY };
        }
      };
      const onMove = (e) => {
        if (dragging) {
          setPos(e.clientX);
          return;
        }
        if (!pending) return;
        const dx = Math.abs(e.clientX - pending.x);
        const dy = Math.abs(e.clientY - pending.y);
        if (dx > 6 && dx > dy) {
          dragging = true;
          setPos(e.clientX);
        } else if (dy > 10 && dy > dx) {
          pending = null; // vertical intent — let the page scroll
        }
      };
      const onUp = (e) => {
        if (!dragging && pending) {
          const dx = Math.abs(e.clientX - pending.x);
          const dy = Math.abs(e.clientY - pending.y);
          if (dx < 6 && dy < 6) setPos(e.clientX, true); // clean tap — glide the divider there
        }
        dragging = false;
        pending = null;
      };
      ba.addEventListener('pointerdown', onDown);
      window.addEventListener('pointermove', onMove, { passive: true });
      window.addEventListener('pointerup', onUp);
      ba.addEventListener('pointercancel', () => {
        dragging = false;
        pending = null;
      });
      ba.addEventListener('dragstart', (e) => e.preventDefault());

      // Keyboard
      handle?.setAttribute('tabindex', '0');
      handle?.setAttribute('role', 'slider');
      handle?.setAttribute('aria-valuemin', '0');
      handle?.setAttribute('aria-valuemax', '100');
      handle?.setAttribute('aria-valuenow', '50');
      handle?.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        const dir = e.key === 'ArrowLeft' ? -4 : 4;
        const next = Math.max(2, Math.min(98, getPos() + dir));
        ba.classList.remove('is-animating');
        ba.classList.add('is-touched');
        ba.style.setProperty('--pos', next + '%');
        handle.setAttribute('aria-valuenow', String(Math.round(next)));
        e.preventDefault();
      });
    });
  }

  /* ---------- Accordion ---------- */
  function initAccordion() {
    $$('[data-accordion]').forEach((acc) => {
      const items = $$('.acc-item', acc);
      items.forEach((item) => {
        const q = $('.acc-item__q', item);
        const a = $('.acc-item__a', item);
        q?.addEventListener('click', () => {
          const isOpen = item.classList.contains('is-open');
          items.forEach((it) => {
            it.classList.remove('is-open');
            const ans = $('.acc-item__a', it);
            if (ans) ans.style.maxHeight = null;
            $('.acc-item__q', it)?.setAttribute('aria-expanded', 'false');
          });
          if (!isOpen) {
            item.classList.add('is-open');
            if (a) a.style.maxHeight = a.scrollHeight + 'px';
            q.setAttribute('aria-expanded', 'true');
          }
        });
      });
      // open first
      const first = items[0];
      if (first) {
        first.classList.add('is-open');
        const a = $('.acc-item__a', first);
        if (a) a.style.maxHeight = a.scrollHeight + 'px';
      }
    });
  }

  /* ---------- Product gallery ---------- */
  function initGallery() {
    const gallery = $('[data-gallery]');
    if (!gallery) return;
    const main = $('[data-gallery-main]', gallery);
    $$('[data-gallery-thumb]', gallery).forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const src = thumb.getAttribute('data-src');
        if (main && src) main.src = src;
        $$('[data-gallery-thumb]', gallery).forEach((t) => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });
  }

  /* ---------- Collection filter ---------- */
  function initFilters() {
    const bar = $('[data-filters]');
    if (!bar) return;
    const cards = $$('[data-product-cat]');
    $$('[data-filter]', bar).forEach((btn) => {
      btn.addEventListener('click', () => {
        const cat = btn.getAttribute('data-filter');
        $$('[data-filter]', bar).forEach((b) => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        cards.forEach((card) => {
          const match = cat === 'all' || card.getAttribute('data-product-cat') === cat;
          card.style.display = match ? '' : 'none';
        });
      });
    });
  }

  /* ---------- Cart (Shopify AJAX API) ---------- */
  const Cart = {
    formatMoney(cents) {
      const amount = (cents / 100).toLocaleString('he-IL', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
      return '₪' + amount;
    },
    open() {
      const d = $('[data-cart-drawer]');
      d?.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    },
    close() {
      const d = $('[data-cart-drawer]');
      d?.classList.remove('is-open');
      document.body.style.overflow = '';
    },
    async get() {
      const res = await fetch('/cart.js');
      return res.json();
    },
    async add(id, quantity = 1) {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, quantity }),
      });
      if (!res.ok) throw new Error('add failed');
      return res.json();
    },
    async change(key, quantity) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      return res.json();
    },
    updateShipBar(cart) {
      const bar = $('[data-ship-bar]');
      if (!bar) return;
      const threshold = parseInt(bar.getAttribute('data-threshold'), 10) || 0;
      if (threshold <= 0) return;
      const total = cart ? cart.total_price : 0;
      const left = threshold - total;
      const fill = $('[data-ship-fill]', bar);
      const msg = $('[data-ship-msg]', bar);
      if (fill) fill.style.width = Math.min(100, (total / threshold) * 100) + '%';
      bar.classList.toggle('is-done', left <= 0);
      if (msg) {
        if (total <= 0) msg.textContent = bar.getAttribute('data-base-msg') || '';
        else if (left <= 0) msg.textContent = 'מגיע לכם משלוח חינם! 🎉';
        else msg.innerHTML = 'עוד <b class="tnum">' + this.formatMoney(left) + '</b> למשלוח חינם';
      }
    },
    async saveNote(note) {
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note }),
        });
      } catch (e) {
        /* offline */
      }
    },
    restoreGiftNote(cart) {
      const toggle = $('[data-gift-toggle]');
      const note = $('[data-gift-note]');
      if (!toggle || !note) return;
      if (cart && cart.note && !note.value) {
        toggle.checked = true;
        note.value = cart.note;
        note.style.display = '';
      }
    },
    updateUpsells(cart) {
      const wrap = $('[data-cart-upsell]');
      if (!wrap) return;
      const inCart = new Set((cart ? cart.items : []).map((i) => String(i.variant_id)));
      let visible = 0;
      $$('[data-upsell-add]', wrap).forEach((btn) => {
        const hidden = inCart.has(btn.getAttribute('data-upsell-add'));
        btn.style.display = hidden ? 'none' : '';
        if (!hidden) visible += 1;
      });
      wrap.style.display = visible && cart && cart.items.length ? '' : 'none';
    },
    render(cart) {
      const body = $('[data-cart-body]');
      const foot = $('[data-cart-foot]');
      const countEls = $$('[data-cart-count]');
      if (!body) return;
      countEls.forEach((el) => {
        const prev = el.textContent;
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? '' : 'none';
        if (cart.item_count > 0 && prev !== String(cart.item_count)) {
          el.classList.remove('is-bumping');
          void el.offsetWidth;
          el.classList.add('is-bumping');
        }
      });
      this.updateShipBar(cart);
      this.updateUpsells(cart);
      this.restoreGiftNote(cart);
      if (!cart.items.length) {
        body.innerHTML =
          '<div class="cart-drawer__empty"><p>העגלה שלך ריקה</p><a href="/collections/all" class="btn btn--outline btn--sm" data-cart-close>המשיכו לקנייה</a></div>';
        if (foot) foot.style.display = 'none';
        bindClose();
        return;
      }
      if (foot) foot.style.display = '';
      body.innerHTML = cart.items
        .map(
          (item) => `
        <div class="cart-line">
          <img class="cart-line__img" src="${item.image ? item.image.replace(/(\.[^.]+)$/, '_160x$1') : ''}" alt="${item.product_title}" loading="lazy">
          <div class="cart-line__info">
            <span class="cart-line__title">${item.product_title}</span>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<span class="card__tagline">${item.variant_title}</span>` : ''}
            <span class="price">${this.formatMoney(item.final_line_price)}</span>
            <div class="cart-line__qty">
              <button data-qty-dec="${item.key}" aria-label="הפחת">−</button>
              <span class="tnum">${item.quantity}</span>
              <button data-qty-inc="${item.key}" aria-label="הוסף">+</button>
            </div>
          </div>
          <button class="cart-line__remove" data-qty-remove="${item.key}" aria-label="הסר">✕</button>
        </div>`
        )
        .join('');
      const sub = $('[data-cart-subtotal]');
      if (sub) sub.textContent = this.formatMoney(cart.total_price);
      bindLineActions();
      bindClose();
    },
    async refresh() {
      try {
        const cart = await this.get();
        this.render(cart);
      } catch (e) {
        /* offline / no store */
      }
    },
  };

  function bindClose() {
    $$('[data-cart-close]').forEach((b) =>
      b.addEventListener('click', () => Cart.close())
    );
  }
  function bindLineActions() {
    $$('[data-qty-inc]').forEach((b) =>
      b.addEventListener('click', async () => {
        const key = b.getAttribute('data-qty-inc');
        const line = b.closest('.cart-line');
        const cur = parseInt($('.tnum', line.querySelector('.cart-line__qty')).textContent, 10);
        Cart.render(await Cart.change(key, cur + 1));
      })
    );
    $$('[data-qty-dec]').forEach((b) =>
      b.addEventListener('click', async () => {
        const key = b.getAttribute('data-qty-dec');
        const line = b.closest('.cart-line');
        const cur = parseInt($('.tnum', line.querySelector('.cart-line__qty')).textContent, 10);
        Cart.render(await Cart.change(key, Math.max(0, cur - 1)));
      })
    );
    $$('[data-qty-remove]').forEach((b) =>
      b.addEventListener('click', async () => {
        Cart.render(await Cart.change(b.getAttribute('data-qty-remove'), 0));
      })
    );
  }

  function initCart() {
    $$('[data-cart-open]').forEach((b) =>
      b.addEventListener('click', (e) => {
        e.preventDefault();
        Cart.open();
        Cart.refresh();
      })
    );
    $('[data-cart-scrim]')?.addEventListener('click', () => Cart.close());
    bindClose(); // bind the static close button too (refresh may fail offline)

    // Gift note — toggles the textarea and saves to the Shopify cart note (debounced)
    const giftToggle = $('[data-gift-toggle]');
    const giftNote = $('[data-gift-note]');
    if (giftToggle && giftNote) {
      let noteTimer;
      giftToggle.addEventListener('change', () => {
        giftNote.style.display = giftToggle.checked ? '' : 'none';
        if (giftToggle.checked) {
          giftNote.focus();
        } else {
          giftNote.value = '';
          Cart.saveNote('');
        }
      });
      giftNote.addEventListener('input', () => {
        clearTimeout(noteTimer);
        noteTimer = setTimeout(() => Cart.saveNote(giftNote.value.trim()), 600);
      });
    }

    // One-tap cart upsells (buttons are static markup in the drawer foot)
    $$('[data-upsell-add]').forEach((btn) =>
      btn.addEventListener('click', async () => {
        const id = btn.getAttribute('data-upsell-add');
        if (!id || btn.disabled) return;
        btn.disabled = true;
        try {
          await Cart.add(id, 1);
          await Cart.refresh();
        } catch (e) {
          /* offline / unavailable — leave silently */
        }
        btn.disabled = false;
      })
    );

    // Add-to-cart forms & quick-add buttons
    $$('[data-add-to-cart]').forEach((form) => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const id = form.querySelector('[name="id"]')?.value;
        const qty = parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10);
        if (!id) return;
        const btn = form.querySelector('[type="submit"]');
        const label = btn?.textContent;
        if (btn) {
          btn.disabled = true;
          btn.textContent = 'מוסיף…';
        }
        try {
          await Cart.add(id, qty);
          await addAddonsToCart(form);
          if (btn) {
            btn.textContent = 'נוסף לעגלה ✓';
            btn.classList.add('is-added');
          }
          Cart.open();
          await Cart.refresh();
        } catch (err) {
          if (btn) btn.textContent = 'לא ניתן להוסיף';
        } finally {
          if (btn) {
            setTimeout(() => {
              btn.disabled = false;
              btn.textContent = label;
              btn.classList.remove('is-added');
            }, 1400);
          }
        }
      });
    });

    Cart.refresh();
  }

  /* ---------- Product options (size / addons) ---------- */
  function initProductOptions() {
    const form = $('[data-product-form]');
    if (!form) return;
    // size pills -> update hidden variant id + price
    $$('[data-variant-pill]', form).forEach((pill) => {
      pill.addEventListener('click', () => {
        $$('[data-variant-pill]', form).forEach((p) => p.classList.remove('is-active'));
        pill.classList.add('is-active');
        const id = pill.getAttribute('data-variant-id');
        const idInput = form.querySelector('[name="id"]');
        if (id && idInput) idInput.value = id;
        const price = pill.getAttribute('data-variant-price');
        const priceEl = $('[data-current-price]');
        if (price && priceEl) priceEl.textContent = price;
      });
    });
  }

  /* ---------- Back to top ---------- */
  function initBackTop() {
    const btn = $('[data-back-top]');
    if (!btn) return;
    const toggle = () => btn.classList.toggle('is-visible', window.scrollY > innerHeight * 0.8);
    toggle();
    window.addEventListener('scroll', toggle, { passive: true });
    btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: prefersReduced ? 'auto' : 'smooth' }));
  }

  /* ---------- Quantity picker ---------- */
  function initQty() {
    $$('[data-qty]').forEach((qty) => {
      const input = $('input', qty);
      $('[data-qty-plus]', qty)?.addEventListener('click', () => {
        input.value = Math.min(99, parseInt(input.value || '1', 10) + 1);
        input.dispatchEvent(new Event('change'));
      });
      $('[data-qty-minus]', qty)?.addEventListener('click', () => {
        input.value = Math.max(1, parseInt(input.value || '1', 10) - 1);
        input.dispatchEvent(new Event('change'));
      });
    });
  }

  /* ---------- Sticky mobile add-to-cart ---------- */
  function initStickyAtc() {
    const bar = $('[data-sticky-atc]');
    const anchor = $('[data-atc-anchor]');
    if (!bar || !anchor) return;
    const toggle = () => {
      const visible = anchor.getBoundingClientRect().bottom < 0;
      bar.classList.toggle('is-visible', visible);
      document.body.classList.toggle('has-sticky-atc', visible);
    };
    toggle();
    window.addEventListener('scroll', toggle, { passive: true });
    $('[data-sticky-atc-btn]', bar)?.addEventListener('click', () => {
      const form = $('[data-product-form]');
      if (form && form.requestSubmit) form.requestSubmit();
      else if (form) form.submit();
    });
  }

  /* ---------- Gallery zoom ---------- */
  function initGalleryZoom() {
    const main = $('.gallery__main');
    if (!main) return;
    main.addEventListener('click', (e) => {
      if (prefersReduced) return;
      const rect = main.getBoundingClientRect();
      main.style.setProperty('--zx', ((e.clientX - rect.left) / rect.width) * 100 + '%');
      main.style.setProperty('--zy', ((e.clientY - rect.top) / rect.height) * 100 + '%');
      main.classList.toggle('is-zoomed');
    });
    main.addEventListener('mouseleave', () => main.classList.remove('is-zoomed'));
  }

  /* ---------- Product add-ons (multi-add) ---------- */
  async function addAddonsToCart(form) {
    const checked = $$('[data-addon-handle]:checked', form);
    for (const box of checked) {
      const handle = box.getAttribute('data-addon-handle');
      try {
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) continue;
        const p = await res.json();
        const variant = p.variants && p.variants[0];
        if (variant) await Cart.add(variant.id, 1);
      } catch (e) {
        /* offline or product missing — skip silently */
      }
    }
  }

  /* ---------- Bundle offer (one-click multi-add) ---------- */
  function initBundle() {
    $$('[data-bundle-add]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const handles = (btn.getAttribute('data-bundle-handles') || '')
          .split(',')
          .map((h) => h.trim())
          .filter(Boolean);
        if (!handles.length || btn.disabled) return;
        btn.disabled = true;
        const originalText = btn.innerHTML;
        try {
          const items = [];
          for (const handle of handles) {
            const res = await fetch(`/products/${handle}.js`);
            if (!res.ok) throw new Error('product missing: ' + handle);
            const p = await res.json();
            const variant = (p.variants || []).find((v) => v.available) || p.variants?.[0];
            if (!variant) throw new Error('no variant: ' + handle);
            items.push({ id: variant.id, quantity: 1 });
          }
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items }),
          });
          if (!res.ok) throw new Error('add failed');
          await Cart.refresh();
          Cart.open();
        } catch (e) {
          btn.innerHTML = 'משהו השתבש — נסו שוב';
          setTimeout(() => {
            btn.innerHTML = originalText;
          }, 2500);
        }
        btn.disabled = false;
      });
    });
  }

  /* ---------- Setup builder (בנו את הסטאפ שלכם) ---------- */
  function initSetupBuilder() {
    $$('[data-builder]').forEach((builder) => {
      const items = $$('[data-builder-item]', builder);
      const countEl = $('[data-builder-count]', builder);
      const totalEl = $('[data-builder-total]', builder);
      const addBtn = $('[data-builder-add]', builder);
      if (!items.length || !addBtn) return;

      const update = () => {
        const selected = items.filter((i) => i.classList.contains('is-selected'));
        const total = selected.reduce((sum, i) => sum + (parseInt(i.getAttribute('data-price'), 10) || 0), 0);
        if (countEl) {
          if (!selected.length) countEl.textContent = 'לא נבחרו פריטים';
          else if (selected.length === 1) countEl.textContent = 'פריט אחד נבחר';
          else countEl.textContent = selected.length + ' פריטים נבחרו';
        }
        if (totalEl) totalEl.textContent = Cart.formatMoney(total);
        addBtn.disabled = !selected.length;
      };

      items.forEach((item) =>
        item.addEventListener('click', () => {
          item.classList.toggle('is-selected');
          item.setAttribute('aria-pressed', item.classList.contains('is-selected') ? 'true' : 'false');
          update();
        })
      );

      addBtn.addEventListener('click', async () => {
        const selected = items.filter((i) => i.classList.contains('is-selected'));
        if (!selected.length || addBtn.disabled) return;
        addBtn.disabled = true;
        const original = addBtn.innerHTML;
        const cartItems = [];
        for (const item of selected) {
          const variantId = item.getAttribute('data-variant-id');
          if (variantId) {
            cartItems.push({ id: parseInt(variantId, 10), quantity: 1 });
            continue;
          }
          // No live variant rendered — resolve by handle at click time
          try {
            const res = await fetch(`/products/${item.getAttribute('data-handle')}.js`);
            if (!res.ok) continue;
            const p = await res.json();
            const variant = (p.variants || []).find((v) => v.available) || p.variants?.[0];
            if (variant) cartItems.push({ id: variant.id, quantity: 1 });
          } catch (e) {
            /* skip unavailable item */
          }
        }
        try {
          if (!cartItems.length) throw new Error('nothing addable');
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: cartItems }),
          });
          if (!res.ok) throw new Error('add failed');
          await Cart.refresh();
          Cart.open();
        } catch (e) {
          addBtn.innerHTML = 'משהו השתבש — נסו שוב';
          setTimeout(() => {
            addBtn.innerHTML = original;
          }, 2500);
        }
        addBtn.disabled = false;
        update();
      });

      update();
    });
  }

  /* ---------- Accessibility widget ---------- */
  const A11Y_KEY = 'minima-a11y-v1';
  const A11Y_TOGGLES = [
    'contrast-high',
    'contrast-inverted',
    'grayscale',
    'highlight-links',
    'readable-font',
    'big-cursor',
    'stop-animations',
  ];

  function loadA11y() {
    try {
      const raw = localStorage.getItem(A11Y_KEY);
      return raw ? JSON.parse(raw) : { scale: 0, toggles: {} };
    } catch (e) {
      return { scale: 0, toggles: {} };
    }
  }
  function saveA11y(state) {
    try {
      localStorage.setItem(A11Y_KEY, JSON.stringify(state));
    } catch (e) {
      /* ignore */
    }
  }
  function applyA11y(state) {
    const root = document.documentElement;
    A11Y_TOGGLES.forEach((name) => root.classList.toggle('a11y-' + name, !!state.toggles[name]));
    const scale = Math.max(0, Math.min(4, state.scale || 0));
    root.setAttribute('data-a11y-text-scale', String(scale));
    $$('[data-a11y-toggle]').forEach((btn) => {
      const name = btn.getAttribute('data-a11y-toggle');
      btn.classList.toggle('is-active', !!state.toggles[name]);
    });
  }

  function initA11y() {
    const panel = $('[data-a11y-panel]');
    const openBtn = $('[data-a11y-open]');
    if (!panel || !openBtn) return;

    let state = loadA11y();
    applyA11y(state);

    const open = () => {
      panel.classList.add('is-open');
      openBtn.setAttribute('aria-expanded', 'true');
    };
    const close = () => {
      panel.classList.remove('is-open');
      openBtn.setAttribute('aria-expanded', 'false');
    };

    openBtn.addEventListener('click', () => {
      panel.classList.contains('is-open') ? close() : open();
    });
    $('[data-a11y-close]')?.addEventListener('click', close);
    document.addEventListener('click', (e) => {
      if (panel.classList.contains('is-open') && !panel.contains(e.target) && e.target !== openBtn && !openBtn.contains(e.target)) {
        close();
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close();
    });

    $$('[data-a11y-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const name = btn.getAttribute('data-a11y-toggle');
        state.toggles[name] = !state.toggles[name];
        saveA11y(state);
        applyA11y(state);
      });
    });

    $('[data-a11y-text-inc]')?.addEventListener('click', () => {
      state.scale = Math.min(4, (state.scale || 0) + 1);
      saveA11y(state);
      applyA11y(state);
    });
    $('[data-a11y-text-dec]')?.addEventListener('click', () => {
      state.scale = Math.max(0, (state.scale || 0) - 1);
      saveA11y(state);
      applyA11y(state);
    });
    $('[data-a11y-reset]')?.addEventListener('click', () => {
      state = { scale: 0, toggles: {} };
      saveA11y(state);
      applyA11y(state);
    });
  }

  /* ---------- Animated counters ---------- */
  function initCounters() {
    const els = $$('[data-count]');
    if (!els.length) return;
    const run = (el) => {
      const target = parseInt(el.getAttribute('data-count'), 10) || 0;
      if (prefersReduced || !viewportH()) {
        el.textContent = target.toLocaleString('he-IL');
        return;
      }
      const dur = 1200;
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - t, 3);
        el.textContent = Math.round(target * eased).toLocaleString('he-IL');
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };
    let pending = els.slice();
    const check = () => {
      const limit = viewportH() || 0;
      pending = pending.filter((el) => {
        if (!limit || el.getBoundingClientRect().top < limit - 40) {
          run(el);
          return false;
        }
        return true;
      });
      if (!pending.length) {
        window.removeEventListener('scroll', check);
        clearInterval(heartbeat);
      }
    };
    window.addEventListener('scroll', check, { passive: true });
    const heartbeat = setInterval(() => {
      if (!pending.length) clearInterval(heartbeat);
      else check();
    }, 700);
    check();
  }

  /* ---------- Magnetic buttons ---------- */
  function initMagnetic() {
    if (prefersReduced) return;
    $$('[data-magnetic]').forEach((el) => {
      el.addEventListener('mousemove', (e) => {
        const r = el.getBoundingClientRect();
        const x = (e.clientX - (r.left + r.width / 2)) * 0.25;
        const y = (e.clientY - (r.top + r.height / 2)) * 0.25;
        el.style.transform = `translate(${x}px, ${y}px)`;
      });
      el.addEventListener('mouseleave', () => (el.style.transform = ''));
    });
  }

  /* ---------- Init ---------- */
  function init() {
    initReveal();
    initHeader();
    initMobileNav();
    initHeroTyping();
    initBeforeAfter();
    initAccordion();
    initGallery();
    initFilters();
    initCart();
    initProductOptions();
    initMagnetic();
    initBackTop();
    initQty();
    initStickyAtc();
    initGalleryZoom();
    initBundle();
    initSetupBuilder();
    initCounters();
    initA11y();
    initCardTilt();
    initHeroParallax();
  }

  /* ---------- Enhanced motion (2026-07-25) ---------- */
  const finePointer = () =>
    window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  function initCardTilt() {
    if (prefersReduced || !finePointer()) return;
    $$('.card').forEach((card) => {
      if (card.dataset.tilt) return;
      card.dataset.tilt = '1';
      const media = $('.card__media', card);
      if (!media) return;
      card.addEventListener('mousemove', (e) => {
        const r = card.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        media.style.transform =
          'perspective(760px) rotateX(' + (-py * 6).toFixed(2) +
          'deg) rotateY(' + (px * 8).toFixed(2) + 'deg)';
      });
      card.addEventListener('mouseleave', () => { media.style.transform = ''; });
    });
  }

  function initHeroParallax() {
    if (prefersReduced || window.__heroPx) return;
    window.__heroPx = true;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const content = document.querySelector('.hero__content');
        const y = window.scrollY || 0;
        if (content && y < window.innerHeight) {
          content.style.transform = 'translateY(' + (y * 0.18).toFixed(1) + 'px)';
          content.style.opacity = String(Math.max(0, 1 - y / (window.innerHeight * 0.7)));
        }
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  function initCursorGlow() {
    if (prefersReduced || !finePointer() || $('.cursor-glow')) return;
    const glow = document.createElement('div');
    glow.className = 'cursor-glow';
    document.body.appendChild(glow);
    let tx = 0, ty = 0, cx = 0, cy = 0, on = false, raf = null;
    const loop = () => {
      cx += (tx - cx) * 0.15;
      cy += (ty - cy) * 0.15;
      glow.style.transform = 'translate(' + cx + 'px,' + cy + 'px)';
      raf = requestAnimationFrame(loop);
    };
    window.addEventListener('mousemove', (e) => {
      tx = e.clientX; ty = e.clientY;
      if (!on) { on = true; glow.classList.add('is-on'); }
      if (!raf) loop();
    }, { passive: true });
    window.addEventListener('mouseout', (e) => {
      if (!e.relatedTarget) { glow.classList.remove('is-on'); on = false; }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for section rendering in the theme editor
  document.addEventListener('shopify:section:load', init);
})();
